/** Top-level path segments that must never be treated as location/country slugs. */
export const RESERVED_TOP_SEGMENTS = new Set([
  "services",
  "solutions",
  "blog",
  "career",
  "about-us",
  "contact-us",
  "portfolio",
  "website-portfolio",
  "seo-portfolio",
  "api",
  "media",
  "resumes",
  "privacy-policy",
  "thank-you",
  "calendar",
]);

export const SEO_COUNTRY_SLUG_HEADER = "x-seo-country-slug";
export const SEO_COUNTRY_NAME_HEADER = "x-seo-country-name";
export const SEO_COUNTRY_GLOBAL_HEADER = "x-seo-country-global";
export const SEO_COUNTRY_CODE_HEADER = "x-seo-country-code";
export const SEO_LOCATION_SLUG_HEADER = "x-seo-location-slug";
export const SEO_LOCATION_NAME_HEADER = "x-seo-location-name";
export const SEO_LOCATION_DISPLAY_NAME_HEADER = "x-seo-location-display-name";
export const SEO_LOCATION_TYPE_HEADER = "x-seo-location-type";
export const SEO_ORIGINAL_PATHNAME_HEADER = "x-seo-original-pathname";

export type LocationMatchType = "city" | "state";

export type MatchedSeoLocation = {
  mappingName: string;
  mappingSlug: string;
  mappingDisplayName: string;
  matchType: LocationMatchType;
};

export type ResolvedSeoCountry = {
  slug: string;
  countryDisplayName: string;
  countryCode: string;
  isGlobalCountry: boolean;
  matchedLocation: MatchedSeoLocation | null;
};

export type CountryPathInput = ResolvedSeoCountry | null | undefined;

type SeoCityStateMappingRow = {
  mappingName: string;
  mappingSlug: string;
  mappingDisplayName: string;
  mappingKind: LocationMatchType;
};

type SeoCountryRow = {
  slug?: string;
  countryDisplayName?: string;
  countryCode?: string;
  isGlobalCountry?: boolean;
  cityAndStateMappingList?: SeoCityStateMappingRow[];
};

function normalizeSlug(value: string | undefined | null): string {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function normalizeName(value: string | undefined | null): string {
  return String(value ?? "").trim().toLowerCase();
}

function getMappings(
  row: SeoCountryRow,
  kind: LocationMatchType
): SeoCityStateMappingRow[] {
  const list = Array.isArray(row.cityAndStateMappingList)
    ? row.cityAndStateMappingList
    : [];
  return list
    .map((item) => {
      const mappingName = String(item?.mappingName ?? "").trim();
      const mappingSlug = normalizeSlug(item?.mappingSlug);
      const mappingDisplayName = String(
        item?.mappingDisplayName ?? ""
      ).trim();
      const mappingKind = String(item?.mappingKind ?? "").trim().toLowerCase();
      if (!mappingName || !mappingSlug || !mappingDisplayName) return null;
      if (mappingKind !== "city" && mappingKind !== "state") return null;
      if (mappingKind !== kind) return null;
      return {
        mappingName,
        mappingSlug,
        mappingDisplayName,
        mappingKind: mappingKind as LocationMatchType,
      };
    })
    .filter(Boolean) as SeoCityStateMappingRow[];
}

function toMatchedLocation(
  mapping: SeoCityStateMappingRow
): MatchedSeoLocation {
  return {
    mappingName: mapping.mappingName,
    mappingSlug: mapping.mappingSlug,
    mappingDisplayName: mapping.mappingDisplayName,
    matchType: mapping.mappingKind,
  };
}

function toResolvedCountry(
  row: SeoCountryRow,
  matchedLocation: MatchedSeoLocation | null = null
): ResolvedSeoCountry {
  return {
    slug: String(row.slug).trim(),
    countryDisplayName: String(
      row.countryDisplayName || row.countryCode || row.slug
    ).trim(),
    countryCode: String(row.countryCode || "").trim(),
    isGlobalCountry: Boolean(row.isGlobalCountry),
    matchedLocation,
  };
}

/** URL prefix: city/state slug when matched, else non-global country slug. */
export function getSeoUrlPrefix(country: CountryPathInput): string {
  if (!country) return "";
  if (country.matchedLocation?.mappingSlug) {
    return country.matchedLocation.mappingSlug;
  }
  if (country.isGlobalCountry) return "";
  return country.slug;
}

export function isReservedTopSegment(segment: string): boolean {
  return RESERVED_TOP_SEGMENTS.has(String(segment || "").toLowerCase());
}

/**
 * All URL prefixes that can appear as `/{prefix}/…` (non-global countries +
 * city/state mapping slugs). Used by `generateStaticParams` for country pages.
 */
export function listSeoUrlPrefixes(
  countries: Array<SeoCountryRow & { status?: boolean }> | undefined
): string[] {
  const prefixes = new Set<string>();

  for (const row of countries ?? []) {
    if (!row?.slug?.trim() || row.status === false) continue;

    if (!row.isGlobalCountry) {
      const countrySlug = normalizeSlug(row.slug);
      if (countrySlug && !isReservedTopSegment(countrySlug)) {
        prefixes.add(countrySlug);
      }
    }

    for (const mapping of row.cityAndStateMappingList ?? []) {
      const mappingSlug = normalizeSlug(mapping?.mappingSlug);
      if (!mappingSlug || isReservedTopSegment(mappingSlug)) continue;
      const kind = String(mapping?.mappingKind ?? "")
        .trim()
        .toLowerCase();
      if (kind !== "city" && kind !== "state") continue;
      prefixes.add(mappingSlug);
    }
  }

  return [...prefixes];
}

export function isCountryServicesSegment(segment: string): boolean {
  return String(segment || "").toLowerCase() === "services";
}

export function isCountrySolutionsSegment(segment: string): boolean {
  return String(segment || "").toLowerCase() === "solutions";
}

/**
 * Resolve from a path segment.
 * Order: city mappingSlug → state mappingSlug → country slug.
 */
export function resolveCountryFromPathSegment(
  countries: SeoCountryRow[] | undefined,
  segment: string | undefined | null
): ResolvedSeoCountry | null {
  if (!Array.isArray(countries) || !countries.length) return null;
  const token = normalizeSlug(segment);
  if (!token || isReservedTopSegment(token)) return null;

  for (const row of countries) {
    if (!row?.slug?.trim()) continue;
    const city = getMappings(row, "city").find((m) => m.mappingSlug === token);
    if (city) {
      return toResolvedCountry(row, toMatchedLocation(city));
    }
  }

  for (const row of countries) {
    if (!row?.slug?.trim()) continue;
    const state = getMappings(row, "state").find((m) => m.mappingSlug === token);
    if (state) {
      return toResolvedCountry(row, toMatchedLocation(state));
    }
  }

  const country = countries.find(
    (c) => normalizeSlug(c.slug) === token && c.slug?.trim()
  );
  if (!country?.slug?.trim()) return null;
  return toResolvedCountry(country, null);
}

export function resolveCountryFromCode(
  countries: SeoCountryRow[] | undefined,
  countryCode: string | undefined | null
): ResolvedSeoCountry | null {
  if (!Array.isArray(countries) || !countries.length) return null;
  const normalizedCode = String(countryCode || "").trim().toUpperCase();
  if (!normalizedCode) return null;

  const row = countries.find(
    (c) => c.countryCode?.trim().toUpperCase() === normalizedCode && c.slug?.trim()
  );
  if (!row?.slug?.trim()) return null;
  return toResolvedCountry(row, null);
}

/**
 * Resolve from geo IP.
 * Order: city → state → country code.
 */
export function resolveCountryFromGeo(
  countries: SeoCountryRow[] | undefined,
  geo: {
    city: string;
    region: string;
    code: string;
  }
): ResolvedSeoCountry | null {
  if (!Array.isArray(countries) || !countries.length) return null;

  const cityName = normalizeName(geo.city);
  if (cityName) {
    for (const row of countries) {
      if (!row?.slug?.trim()) continue;
      const city = getMappings(row, "city").find(
        (m) =>
          normalizeName(m.mappingName) === cityName ||
          m.mappingSlug === normalizeSlug(geo.city)
      );
      if (city) {
        return toResolvedCountry(row, toMatchedLocation(city));
      }
    }
  }

  const regionName = normalizeName(geo.region);
  if (regionName) {
    for (const row of countries) {
      if (!row?.slug?.trim()) continue;
      const state = getMappings(row, "state").find(
        (m) =>
          normalizeName(m.mappingName) === regionName ||
          m.mappingSlug === normalizeSlug(geo.region)
      );
      if (state) {
        return toResolvedCountry(row, toMatchedLocation(state));
      }
    }
  }

  return resolveCountryFromCode(countries, geo.code);
}

export function resolveGlobalCountry(
  countries: SeoCountryRow[] | undefined
): ResolvedSeoCountry | null {
  if (!Array.isArray(countries) || !countries.length) return null;
  const row = countries.find((c) => c.isGlobalCountry && c.slug?.trim());
  if (!row?.slug?.trim()) return null;
  return toResolvedCountry(row, null);
}

export function countryHomePath(country: CountryPathInput): string {
  const prefix = getSeoUrlPrefix(country);
  return prefix ? `/${prefix}` : "/";
}

export function countryServicesListPath(country: CountryPathInput): string {
  const prefix = getSeoUrlPrefix(country);
  return prefix ? `/${prefix}/services` : "/services";
}

export function countryServicePath(
  country: CountryPathInput,
  serviceSlug: string
): string {
  const prefix = getSeoUrlPrefix(country);
  const service = String(serviceSlug || "").trim().replace(/^\/+|\/+$/g, "");
  if (!service) return countryServicesListPath(country);
  return prefix ? `/${prefix}/${service}` : `/${service}`;
}

/** Solutions are global-only — ignore country/city prefixes. */
export function countrySolutionsListPath(_country?: CountryPathInput): string {
  return "/solutions";
}

/** Solutions are global-only — ignore country/city prefixes. */
export function countrySolutionPath(
  _country: CountryPathInput,
  solutionSlug: string
): string {
  const solution = String(solutionSlug || "").trim().replace(/^\/+|\/+$/g, "");
  if (!solution) return "/solutions";
  return `/${solution}`;
}

/** True when the current path already uses this country’s SEO URL prefix. */
export function isActiveSeoUrlPrefix(
  pathname: string | undefined | null,
  country: CountryPathInput
): boolean {
  const prefix = getSeoUrlPrefix(country);
  if (!prefix) return false;
  const first = String(pathname || "")
    .split("/")
    .filter(Boolean)[0]
    ?.toLowerCase();
  return first === prefix.toLowerCase();
}

export type WithCountryHrefOptions = {
  /** Known service slugs — these get city/country prefixes. */
  serviceSlugs?: ReadonlySet<string> | null;
  /** Known solution slugs — never get city/country prefixes. */
  solutionSlugs?: ReadonlySet<string> | null;
};

/** Prefix internal paths with the resolved URL location/country prefix. */
export function withCountryHref(
  href: string | undefined | null,
  country: CountryPathInput,
  options?: WithCountryHrefOptions
): string {
  const raw = String(href ?? "").trim();
  if (!raw) return "#";

  if (
    /^(https?:|mailto:|tel:|whatsapp:)/i.test(raw) ||
    raw.startsWith("#") ||
    raw.startsWith("?")
  ) {
    return raw;
  }

  const prefix = getSeoUrlPrefix(country);
  if (!prefix) return raw;

  let pathname = raw;
  let search = "";
  let hash = "";

  const hashIdx = pathname.indexOf("#");
  if (hashIdx >= 0) {
    hash = pathname.slice(hashIdx);
    pathname = pathname.slice(0, hashIdx);
  }
  const queryIdx = pathname.indexOf("?");
  if (queryIdx >= 0) {
    search = pathname.slice(queryIdx);
    pathname = pathname.slice(0, queryIdx);
  }

  const path = pathname.replace(/\/+$/, "") || "/";
  const segments = path.split("/").filter(Boolean);

  if (segments[0]?.toLowerCase() === prefix.toLowerCase()) {
    return raw;
  }

  if (path === "/") {
    return `${countryHomePath(country)}${search}${hash}`;
  }

  const firstLower = String(segments[0] || "").toLowerCase();

  if (segments.length === 1 && isCountryServicesSegment(firstLower)) {
    return `${countryServicesListPath(country)}${search}${hash}`;
  }

  // Solutions stay global — never prefix `/solutions` or solution detail slugs.
  if (segments.length === 1 && isCountrySolutionsSegment(firstLower)) {
    return `/solutions${search}${hash}`;
  }

  if (segments.length === 1 && !isReservedTopSegment(firstLower)) {
    if (options?.solutionSlugs?.has(firstLower)) {
      return raw;
    }

    // Only service detail slugs get city/country prefixes.
    if (options?.serviceSlugs && !options.serviceSlugs.has(firstLower)) {
      return raw;
    }

    return `/${prefix}/${segments[0]}${search}${hash}`;
  }

  return raw;
}
